/*
 * Copyright (C) 2025.  SimpliCity Digital Inc - All Rights Reserved
 */

import { useState, useEffect } from "react";
import { Logger } from "cms/utils/logger";
import { useAuth } from "cms/auth/hooks/useAuth";
import { SimpliCitySDK } from "cms/sdk/index";
import dayjs from "dayjs";
// import { SimpliCityAdminSDK } from '@simplicitycms/simplicity-admin-js-sdk'
// import { mockPublicPermitSearchResults, mockPublicPermitSearchResultsLast30Days } from "./mock/use-fetch-permit.enum";

const useFetchPermit = () => {
    const { token } = useAuth();

    const [permits, setPermits] = useState([]);
    const [myPermits, setMyPermits] = useState([]);
    const [isLoading, setIsLoading] = useState([]);
    const [isMyPermitLoading, setIsMyPermitLoading] = useState([]);

    /**
     * fetchPublicPermitDataLast30Days
     * Fetch default Last 30 days permits
     * @returns {Promise<*|void>}
     */
    async function fetchPublicPermitDataLast30Days () {
        // Last 30 days doesn't enough data so getting top 100 data from last year.
        const start_issue_date = dayjs().utc().subtract(1, "year").startOf("day").format();
        const now = dayjs().utc().format();

        const payload = {
            start_issue_date,
            end_issue_date: now,
            page_size: "50",
            page_number: "1",
        };

        const permits = await SimpliCitySDK.services.permit.searchPermit(token, payload);

        return permits;
    }

    /**
     * fetchPrePermitQuery
     * Fetch filtered permits
     * @param filters
     * @returns {Promise<{data: *[], type: string}|*[]>}
     */
    async function fetchPrePermitQuery (filters) {
        try {
            const filterString = filters?.value;

            if (!filterString) {
                return [];
            }

            // Check if string is permit. Permit Prefix is taken from DNV for now.
            // We need to make Simplicity specific enums. For sake of POC its TODO
            const isPermitNumber = checkIfPermitNumber(filterString);

            if (isPermitNumber) {
                const payload = { term: filterString, limit: 10 };
                const permitNumbers = await SimpliCitySDK.services.permit.searchPermitNumber(token, payload);
                const result = {
                    data: [],
                    type: "permit_number"
                };
                for (const permitNumber of permitNumbers) {
                    const _data = { value: permitNumber, label: permitNumber };
                    result.data.push(_data);
                }
                return result;
            }

            // If it's not permit number, we assume it's address
            const payload = { term: filterString, limit: 10 };
            const addressess = await SimpliCitySDK.address.autocompleteAddress(token, payload);
            const result = {
                data: [],
                type: "main_address"
            };
            for (const address of addressess) {
                const _data = { value: address?.address, label: address?.address, address };
                result.data.push(_data);
            }
            return result;
        } catch (error) {
            Logger.error("Failed to load permit data.", error);
        }
    }

    function checkIfPermitNumber (value) {
        const permitNumberPrefixEnum = [
            "ANML",
            "AQUA",
            "BLD",
            "BLST",
            "DPA",
            "ELE",
            "ENV",
            "FLM",
            "FSPR",
            "GAS",
            "HCP",
            "HEAT",
            "MEC",
            "PEST",
            "PLBG",
            "SGN",
            "SOIL",
            "SPU",
            "TREE",
        ];
        return permitNumberPrefixEnum.some((prefix) => value.toLowerCase().includes(prefix.toLocaleLowerCase()));
    }

    /**
     * fetchPrivatePermits
     * returns permits linked to the person who is logged in, identified by the customer security token
     * @returns {Promise<*>}
     */
    async function fetchPrivatePermits () {
        const payload = { page_size: "10", page_number: "1" };
        const _permits = await SimpliCitySDK.services.permit.myPermits(token, payload);
        const permits = _permits.map((permit) => {
            permit.id = permit.permit_id;
            return permit;
        });
        return permits;
    }

    /**
     * fetchPermitByQuery
     *
     * @param query
     * @returns {Promise<void>}
     */
    async function fetchPermitByQuery (query) {
        setIsLoading(true);
        const { type, value } = query;
        const payload = { page_size: "10", page_number: "1" };
        if (type === "main_address") {
            // Search Permit By address
            payload.main_address = value?.value;
            payload.address = value?.address;
        }
        if (type === "permit_number") {
            // Search Permit By Permit number
            payload.permit_number = value?.value || value;
        }
        const _permits = await SimpliCitySDK.services.permit.searchPermit(token, payload);
        const permits = _permits.map((permit) => {
            permit.id = permit.permit_id;
            return permit;
        });
        setPermits(permits);
        setIsLoading(false);
    }

    const fetchDefaultPublicPermits = async () => {
        setIsLoading(true);
        const _permits = await fetchPublicPermitDataLast30Days();
        const permits = _permits.map((permit) => {
            permit.id = permit.permit_id;
            return permit;
        });
        setPermits(permits);
        setIsLoading(false);
    };

    useEffect(() => {
        // FIXME: this ignores login and gets public permits - not suitable for myPermits
        fetchDefaultPublicPermits();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const fetchMyPermits = async () => {
            setIsMyPermitLoading(true);
            const _permits = await fetchPrivatePermits();
            const permits = _permits.map((permit) => {
                permit.id = permit.permit_id;
                return permit;
            });
            setMyPermits(permits);
            setIsMyPermitLoading(false);
        };
        fetchMyPermits();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        isLoading,
        isMyPermitLoading,
        permits,
        myPermits,
        fetchPrePermitQuery,
        fetchPrivatePermits,
        fetchPublicPermitDataLast30Days,
        fetchDefaultPublicPermits,
        fetchPermitByQuery,
    };
};

export { useFetchPermit };
